import { useState, useEffect } from 'react';
import { apiGet } from './apiClient';

export interface GlobalWatchlistSummary {
    group_name: string;
    member_count: number;
    oldest_admission: string | null;
    newest_admission: string | null;
}

export interface GlobalWatchlistsResponse {
    watchlists: GlobalWatchlistSummary[];
    total_count: number;
}

export interface GlobalWatchlistMember {
    address: string;
    admitted_at: string;
    primary_score: number | null;
    policy: string | null;
    reason: string | null;
    total_pnl: number | null;
    win_rate: number | null;
    avg_edge: number | null;
    num_trades: number | null;
    num_markets: number | null;
    portfolio_value: number | null;
    first_seen: string;
    last_seen: string;
}

export interface GlobalWatchlistMembersResponse {
    group_name: string;
    members: GlobalWatchlistMember[];
    member_count: number;
}

export function useGlobalWatchlists(): {
    data: GlobalWatchlistsResponse | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<GlobalWatchlistsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        apiGet<GlobalWatchlistsResponse>('/api/watchlists', controller.signal)
            .then(res => {
                if (!cancelled) setData(res);
            })
            .catch(err => {
                if (cancelled || err?.name === 'AbortError') return;
                setError(err.message ?? 'Failed to load watchlists');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, []);

    return { data, loading, error };
}

export function useWatchlistMembers(groupName: string | null): {
    data: GlobalWatchlistMembersResponse | null;
    loading: boolean;
    error: string | null;
} {
    const [data, setData] = useState<GlobalWatchlistMembersResponse | null>(
        null
    );
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!groupName) {
            setData(null);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        setData(null);

        apiGet<GlobalWatchlistMembersResponse>(
            `/api/watchlists/${encodeURIComponent(groupName)}/members`,
            controller.signal
        )
            .then(res => {
                if (!cancelled) setData(res);
            })
            .catch(err => {
                if (cancelled || err?.name === 'AbortError') return;
                setError(err.message ?? 'Failed to load watchlist members');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [groupName]);

    return { data, loading, error };
}
